import React, { createContext, useContext, useState, useEffect } from 'react';
import { useAuth } from './AuthContext';

interface ViewContextType {
  currentViewRole: string;
  setCurrentViewRole: (role: string) => void;
  canSwitchRoles: boolean;
}

const ViewContext = createContext<ViewContextType | undefined>(undefined);

export const ViewProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user } = useAuth();
  const [currentViewRole, setCurrentViewRole] = useState<string>(() => {
    // Admin users can switch roles, others use their actual role
    return user?.role === 'admin' ? 'manager' : (user?.role || 'manager');
  });

  // Update role when user changes (login/logout)
  useEffect(() => {
    if (user) {
      if (user.role === 'admin') {
        // Admin keeps their current view role
        return;
      } else {
        // Non-admin users must use their actual role
        setCurrentViewRole(user.role);
      }
    } else {
      // No user, default to manager
      setCurrentViewRole('manager');
    }
  }, [user]);

  const canSwitchRoles = user?.role === 'admin';

  return (
    <ViewContext.Provider 
      value={{ 
        currentViewRole, 
        setCurrentViewRole, 
        canSwitchRoles 
      }}
    >
      {children}
    </ViewContext.Provider>
  );
};

export const useView = (): ViewContextType => {
  const context = useContext(ViewContext);
  if (!context) {
    throw new Error('useView must be used within a ViewProvider');
  }
  return context;
};